import React from "react";
import { Box, type BoxProps } from "@mui/material";

/**
 * Tiny flex helpers — substitute for MUI Stack which in v9 demands an
 * explicit `component` prop and trips the strict TS overload set the
 * codebase ships with. Row defaults to flex-row, Col to flex-col;
 * everything else is just sx so it stays Box-compatible.
 */
export interface FlexBoxProps extends Omit<BoxProps, "gap"> {
  gap?: number;
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  justify?: "start" | "center" | "end" | "space-between" | "space-around";
  wrap?: boolean;
}

const ALIGN: Record<NonNullable<FlexBoxProps["align"]>, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
  baseline: "baseline",
};
const JUSTIFY: Record<NonNullable<FlexBoxProps["justify"]>, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  "space-between": "space-between",
  "space-around": "space-around",
};

function flex(
  direction: "row" | "column",
  { gap, align, justify, wrap, sx, ...rest }: FlexBoxProps,
) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: direction,
        gap,
        alignItems: align ? ALIGN[align] : undefined,
        justifyContent: justify ? JUSTIFY[justify] : undefined,
        flexWrap: wrap ? "wrap" : undefined,
        ...sx,
      }}
      {...rest}
    />
  );
}

export function Row(props: FlexBoxProps) {
  return flex("row", props);
}
export function Col(props: FlexBoxProps) {
  return flex("column", props);
}
